// gkc_hash_code : 01FJGW9AB4VKN1PM0CVCZY1PTE
import React from "react";
import moment from "moment";
import { API, graphqlOperation } from "aws-amplify";
import get from "lodash/get";
import LocalStorage, { LocalStorageKey } from "../local-storage/local-storage";
import { CommentStatus } from "../common-definitions/common-enum";
import { UpdateCommentInput } from "../API";
import {
  ProgramContext,
  CreateComment,
  AddCommentRealtime,
  UpdateCommentRealtime,
} from "../context/program";
import {
  onModifyCommentByProgramId,
  onCreateCommentByProgramId,
} from "../graphql/subscriptions";

interface UseCommentState {
  commentList: UpdateCommentInput[] | null;
}

const useComment = (programId: string) => {
  const { ProgramState, ProgramDispatch } = React.useContext(ProgramContext);

  const [state, setState] = React.useState<UseCommentState>({
    commentList: null,
  });

  const getValidComments = (comments: UpdateCommentInput[]) => {
    const userId = LocalStorage.get(LocalStorageKey.userId) || "";
    const ngWords = [
      ...(get(ProgramState, "program.customer.items[0].ngWord", "")?.split(
        /[\n,]/
      ) || []),
      ...((ProgramState?.program?.ngWord || "")?.split(/[\n,]/) || []),
    ]
      .filter((word) => word !== "")
      .map((word: string) => word.toLowerCase());
    const ngWordsRegex =
      ngWords.length > 0 ? new RegExp(ngWords.join("|")) : null;
    const programStartTime = moment(
      get(ProgramState, "program.livestreamStartDateTime", "")
    ).valueOf();

    const isLive =
      !ProgramState.program?.livestreamStartDateTime ||
      moment(ProgramState.program?.livestreamStartDateTime).valueOf() <=
        moment().valueOf();

    const commentList = comments.filter((comment: UpdateCommentInput) => {
      const isValid =
        !ngWordsRegex ||
        !(comment?.comment || "").toLowerCase().match(ngWordsRegex);
      const isVisible = comment.status === 0;
      if (isLive) {
        const isBeforeLive =
          moment(parseInt(comment.commentDateTime || "0", 10)).valueOf() <
          programStartTime;
        return (
          ((isVisible && isValid) || comment.userId === userId) && !isBeforeLive
        );
      }
      return (isVisible && isValid) || comment.userId === userId;
    });

    return commentList;
  };

  React.useEffect(() => {
    if (ProgramState?.comments && ProgramState?.program) {
      const commentList = ProgramState?.comments?.sort((a, b) => {
        return (
          moment(parseInt(a.commentDateTime || "0", 10)).valueOf() -
          moment(parseInt(b.commentDateTime || "0", 10)).valueOf()
        );
      });
      setState({
        ...state,
        commentList: getValidComments(commentList),
      });
    }
  }, [ProgramState?.comments, ProgramState?.program]);

  React.useEffect(() => {
    const myCommentsUpdated = state.commentList
      ?.filter(
        (comment) => comment.userId === LocalStorage.get(LocalStorageKey.userId)
      )
      .sort((a, b) => {
        return (
          moment(parseInt(b.updateStatusDateTime || "0", 10)).valueOf() -
          moment(parseInt(a.updateStatusDateTime || "0", 10)).valueOf()
        );
      });
    const isBlockComment =
      get(myCommentsUpdated, "[0].status", CommentStatus.AVAILABLE) ===
      CommentStatus.BLOCK;

    let blockPrograms = LocalStorage.get(LocalStorageKey.blockPrograms)
      ? JSON.parse(LocalStorage.get(LocalStorageKey.blockPrograms) || "")
      : [];

    if (!isBlockComment) {
      blockPrograms = blockPrograms.filter(
        (item: string) => item !== programId
      );
    } else if (!blockPrograms.includes(programId)) {
      blockPrograms.push(programId);
    }
    LocalStorage.set(
      LocalStorageKey.blockPrograms,
      JSON.stringify(blockPrograms)
    );
  }, [state.commentList]);

  React.useEffect(() => {
    // eslint-disable-next-line
    const getCreateComment = (
      API.graphql(
        graphqlOperation(onCreateCommentByProgramId, { programId })
        // eslint-disable-next-line
      ) as any
    ).subscribe({
      // eslint-disable-next-line
      next: (data: any) => {
        const createComment = data.value.data
          .onCreateCommentByProgramId as UpdateCommentInput;
        ProgramDispatch(AddCommentRealtime(createComment));
      },
    });

    return () => {
      getCreateComment.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    // eslint-disable-next-line
    const getUpdateComment = (
      API.graphql(
        graphqlOperation(onModifyCommentByProgramId, { programId })
        // eslint-disable-next-line
      ) as any
    ).subscribe({
      // eslint-disable-next-line
      next: (data: any) => {
        const updateComment = data.value.data
          .onModifyCommentByProgramId as UpdateCommentInput;
        ProgramDispatch(UpdateCommentRealtime(updateComment));
      },
    });

    return () => {
      getUpdateComment.unsubscribe();
    };
  }, []);

  const handleCreateComment = (comment: string, isRehearsal: 0 | 1) => {
    const now = Date.now();
    const blockPrograms = LocalStorage.get(LocalStorageKey.blockPrograms)
      ? JSON.parse(LocalStorage.get(LocalStorageKey.blockPrograms) || "")
      : [];
    ProgramDispatch(
      CreateComment({
        programId,
        dataType: `comment_user#${now}`,
        commentDateTime: `${now}`,
        comment,
        userId: LocalStorage.get(LocalStorageKey.userId) || "",
        userName: LocalStorage.get(LocalStorageKey.userName) || "",
        status: blockPrograms.includes(programId)
          ? CommentStatus.BLOCK
          : CommentStatus.AVAILABLE,
        updateStatusDateTime: `${now}`,
        isRehearsal: isRehearsal || 0,
      })
    );
  };

  return {
    comments: state.commentList,
    createComment: handleCreateComment,
  };
};

export default useComment;
